import React, { useState } from "react";
import CustomMultiSelect from "./multi-select.js";
import Button from "./button.js";
import { getTranslation } from "../../utils/translate.js";

/**
 * AdditionsSection Component
 * 
 * This component displays a section for a group of additions.
 * Users can select additions from the group and override prices.
 */
const AdditionsSection = ({
    group,
    groupAdditions,
    selectedAdditions,
    onAdditionsChange,
    onRemoveGroup,
    onPriceOverrideChange,
    errors,
    language,
    onCreateNewAddition
}) => {
    const [isCreatingAddition, setIsCreatingAddition] = useState(false);
    const [newAddition, setNewAddition] = useState({
        nameHy: "",
        price: ""
    });
    const [newAdditionError, setNewAdditionError] = useState("");

    if (!group) return null;

    const groupId = group.id;
    
    // Options for select components
    const optionsForSelect = groupAdditions.map(add => ({ 
        value: add.id, 
        label: add.nameHy, 
        addition: add
    }));
    
    // Format value for react-select - always as array to support MultiSelect
    const valueForSelect = selectedAdditions.map(add => ({ 
        value: add.id, 
        label: add.nameHy 
    }));

    const errorKey = `additionGroup_${groupId}`;
    const label = `${group.nameHy}`;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Prevent negative values for price
        if (name === "price" && value !== "" && value !== "-" && !isNaN(value) && parseFloat(value) < 0) {
            return; // Don't update if negative
        }
        
        setNewAddition(prev => ({
            ...prev,
            [name]: name === "price" ? (value === "" ? "" : parseFloat(value)) : value
        }));
    };

    const handleAdditionSubmit = () => {
        // Basic validation
        if (!newAddition.nameHy.trim()) {
            setNewAdditionError(getTranslation('validation.required', language) || "Name is required");
            return;
        }
        
        if (newAddition.price === "" || isNaN(newAddition.price) || newAddition.price < 0) {
            setNewAdditionError(getTranslation('validation.priceRequired', language) || "Price is required and must be a positive number");
            return;
        }

        // Here we would usually make an API call to create the addition
        // For now, let's simulate it by passing the data up to the parent
        if (onCreateNewAddition) {
            onCreateNewAddition({
                ...newAddition,
                group: {
                    id: group.id,
                    nameHy: group.nameHy
                }
            });
        }

        // Reset the form
        setNewAddition({ nameHy: "", price: "" });
        setIsCreatingAddition(false);
        setNewAdditionError("");
    };

    return (
        <div className="bg-white dark:bg-[#161b22] rounded-lg p-3 sm:p-4 mb-4 border border-gray-200 dark:border-[#30363d] shadow-sm">
            {/* Mobile-friendly header with stacked layout on small screens */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
                <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-md font-semibold text-gray-700 dark:text-[#b1bac4] mr-2">{label}</h3>
                    <div className="flex flex-wrap gap-1">
                        {group.isRequired && (
                            <span className="bg-red-100 dark:bg-red-950/30 text-red-800 dark:text-red-400 text-xs px-2 py-0.5 rounded-full border dark:border-red-800">
                                {getTranslation('menu.required', language) || 'Required'}
                            </span>
                        )}
                        <span className={`${group.isMulti ? 'bg-blue-100 dark:bg-blue-950/30 text-blue-800 dark:text-blue-400' : 'bg-white dark:bg-[#161b22] text-gray-800 dark:text-[#c9d1d9]'} text-xs px-2 py-0.5 rounded-full border dark:border-[#30363d]`}>
                            {group.isMulti ? 
                                (getTranslation('menu.multiSelect', language) || 'Multi-select') : 
                                (getTranslation('menu.singleSelect', language) || 'Single-select')}
                        </span>
                    </div>
                </div>
                <div className="flex items-center space-x-3 mt-2 sm:mt-0">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsCreatingAddition(!isCreatingAddition)}
                        className="text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        {getTranslation('menu.addNewAddition', language) || 'Add New'}
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemoveGroup(groupId)}
                        className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                    >
                        {getTranslation('common.remove', language) || 'Remove'}
                    </Button>
                </div>
            </div>

            {/* Create new addition form - optimized for mobile */}
            {isCreatingAddition && (
                <div className="mb-4 p-3 border border-blue-100 dark:border-blue-800 rounded-md bg-blue-50 dark:bg-blue-950/30">
                    <h4 className="text-sm font-medium mb-2 text-blue-700 dark:text-blue-400">
                        {getTranslation('menu.createNewAddition', language) || 'Create New Addition'}
                    </h4>
                    <div className="grid grid-cols-1 gap-3">
                        <div>
                            <label htmlFor={`new-addition-name-${groupId}`} className="block text-xs text-gray-600 dark:text-[#8b949e] mb-1">
                                {getTranslation('menu.nameArmenian', language) || 'Name (Armenian)'}*
                            </label>
                            <input
                                id={`new-addition-name-${groupId}`}
                                type="text"
                                name="nameHy"
                                value={newAddition.nameHy}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-[#30363d] rounded-md text-base focus:outline-none focus:ring-1 focus:ring-gray-500 dark:focus:ring-gray-400 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9] placeholder:text-gray-400 dark:placeholder:text-[#6e7681]"
                                placeholder={getTranslation('menu.namePlaceholder', language) || 'Ex: Name'}
                                autoComplete="off"
                            />
                        </div>
                        <div>
                            <label htmlFor={`new-addition-price-${groupId}`} className="block text-xs text-gray-600 dark:text-[#8b949e] mb-1">
                                {getTranslation('menu.priceAMD', language) || 'Price AMD'}*
                            </label>
                            <input
                                id={`new-addition-price-${groupId}`}
                                type="number"
                                name="price"
                                value={newAddition.price}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-[#30363d] rounded-md text-base focus:outline-none focus:ring-1 focus:ring-gray-500 dark:focus:ring-gray-400 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9] placeholder:text-gray-400 dark:placeholder:text-[#6e7681]"
                                placeholder={getTranslation('menu.pricePlaceholder', language) || 'Ex: 500'}
                                min="0"
                                inputMode="numeric"
                                autoComplete="off"
                            />
                        </div>
                    </div>
                    {newAdditionError && (
                        <p className="text-red-500 dark:text-red-400 text-xs mt-1">{newAdditionError}</p>
                    )}
                    <div className="flex justify-end gap-2 mt-3">
                        <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => setIsCreatingAddition(false)}
                        >
                            {getTranslation('common.cancel', language) || 'Cancel'}
                        </Button>
                        <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            onClick={handleAdditionSubmit}
                        >
                            {getTranslation('common.add', language) || 'Add'}
                        </Button>
                    </div>
                </div>
            )}

            {/* Select additions from group */}
            <div className="mb-4">
                <CustomMultiSelect
                    onChange={(selectedOptions) => onAdditionsChange(selectedOptions, group)}
                    value={valueForSelect}
                    name={`additionGroup_${groupId}`}
                    fieldName={`additionGroup_${groupId}`}
                    options={optionsForSelect}
                    placeholder={getTranslation('menu.selectAdditions', language) || 'Select additions'}
                    isRequired={group.isRequired}
                    error={errors && errors[errorKey]}
                    setError={() => onAdditionsChange([], group)}
                    isSingle={false}
                />
            </div>

            {/* Price inputs (only shown if additions are selected) - improved for mobile */}
            {selectedAdditions.length > 0 && (
                <div className="mt-4">
                    <p className="text-sm text-gray-600 dark:text-[#8b949e] mb-2">
                        {getTranslation('menu.changePrices', language) || 'Change prices:'}
                    </p>
                    <div className="space-y-3">
                        {selectedAdditions.map(addition => (
                            <div key={addition.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 border rounded-md bg-white dark:bg-[#0d1117] border-gray-200 dark:border-[#30363d]">
                                <span className="text-sm font-medium text-gray-900 dark:text-[#c9d1d9]">{addition.nameHy || `ID: ${addition.id}`}</span>
                                <div className="flex items-center gap-2 mt-2 sm:mt-0">
                                    <label htmlFor={`price-override-${groupId}-${addition.id}`} className="text-xs text-gray-500 dark:text-[#8b949e] whitespace-nowrap">{getTranslation('menu.changePrice', language) || 'Change price'} (AMD)</label>
                                    <input
                                        id={`price-override-${groupId}-${addition.id}`}
                                        name={`price-override-${groupId}-${addition.id}`}
                                        type="text"
                                        inputMode="decimal"
                                        value={addition.overridedPrice ?? (addition.overridedPrice?.toString() ?? addition.price.toString() ?? '')}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            // Prevent negative values
                                            if (raw !== '' && raw !== '-' && !raw.startsWith('-') && (isNaN(raw) || parseFloat(raw) < 0)) {
                                                return;
                                            }
                                            onPriceOverrideChange(addition.id, raw); // send raw string
                                        }}
                                        onBlur={(e) => {
                                            const raw = e.target.value;
                                            const parsed = raw === "" ? undefined : parseFloat(raw);
                                            // Ensure non-negative value
                                            if (parsed !== undefined && parsed < 0) {
                                                onPriceOverrideChange(addition.id, undefined);
                                                return;
                                            }
                                            onPriceOverrideChange(addition.id, parsed);
                                        }}
                                        className="w-24 px-3 py-2 border border-gray-300 dark:border-[#30363d] rounded-md text-base focus:outline-none focus:ring-1 focus:ring-gray-400 dark:focus:ring-[#30363d] bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9]"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {errors && errors[errorKey] && (
                <p className="text-red-500 dark:text-red-400 text-xs mt-1">{errors[errorKey]}</p>
            )}
        </div>
    );
};

export default AdditionsSection; 
